import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { UseFormReturn, useFieldArray } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import SettingsContext from 'src/features/administration/context/SettingsContext';
import { useListUsers } from 'src/features/auth/api/useListUsers';
import { useListServiceProviders } from 'src/features/serviceProvider/api/useListServiceProviders';
import { useListSites } from 'src/features/sites/api/useListSites';
import { BOOLEAN_MAP, BOOLEAN_OPTIONS } from 'src/features/sites/utils/constants';
import { useListZones } from 'src/features/zone/api/useListZones';
import {
    VISITOR_CATEGORIES_TRANSLATION_MAP,
    ZONE_CAPACITY_TRANSLATION_MAP,
    ZONE_TRANSLATION_MAP,
} from 'src/features/zone/utils/constants';
import { ZoneFormSchemaValues } from 'src/features/zone/utils/formSchema';
import { zoneMessages } from 'src/features/zone/utils/messages';
import { ZONE_TYPE, ZONE_CAPACITY_TYPE, VISITOR_CATEGORY_TYPE } from 'src/features/zone/utils/types';
import { useParking } from 'src/providers/ParkingProvider';
import InputFormField from 'src/registry/components/forms/InputFormField';
import SelectFormField from 'src/registry/components/forms/SelectFormField';
import TextareaFormField from 'src/registry/components/forms/TextareaFormField';
import { Button } from 'src/registry/ui/Button';
import { Form, FormControl, FormControlStack, FormItem, FormLabel, FormMessage, FormField } from 'src/registry/ui/Form';
import {
    MultiSelect,
    MultiSelectControl,
    MultiSelectTrigger,
    MultiSelectContent,
    MultiSelectOption,
} from 'src/registry/ui/MultiSelect';
import { Separator } from 'src/registry/ui/Separator';
import { ICON_MAP } from 'src/utils/constants';
import { translate } from 'src/utils/translate';

type ZoneFormProps = {
    form: UseFormReturn<ZoneFormSchemaValues>;
    onSubmit: (values: ZoneFormSchemaValues) => void;
};

const translationFields = [
    { key: 'name', label: zoneMessages.name },
    { key: 'description', label: zoneMessages.description },
] as const;

function ZoneForm({ onSubmit, form }: ZoneFormProps) {
    const { formatMessage, locale } = useIntl();
    const { parkingId } = useParking();

    const settingsData = React.useContext(SettingsContext);
    const { data: sitesData } = useListSites({ parkingId });
    const { data: serviceProvidersData } = useListServiceProviders({ parkingId });
    const { data: usersData } = useListUsers({});
    const { data: zoneData } = useListZones({ parkingId });

    const { fields } = useFieldArray({
        control: form.control,
        name: 'translations',
    });
    const {
        translations,
        limit_access_per_capacity,
        capacity_limits,
        overflow_spaces,
        limit_user_access,
        total_spaces,
    } = form.watch();

    const siteOptions = React.useMemo(() => {
        if (!sitesData) return [];
        return sitesData.results.map(({ translations, id }) => ({
            label: translate(translations, locale)?.name || '',
            value: id,
        }));
    }, [locale, sitesData]);

    const zoneOptions = React.useMemo(() => {
        if (!zoneData) return [];
        return zoneData.results.map(({ translations, id }) => ({
            label: translate(translations, locale)?.name || '',
            value: id,
        }));
    }, [locale, zoneData]);

    const serviceProviderOptions = React.useMemo(() => {
        if (!serviceProvidersData) return [];
        return serviceProvidersData.results.map(({ name, id }) => ({
            label: name,
            value: id,
        }));
    }, [serviceProvidersData]);

    React.useEffect(() => {
        if (settingsData?.languages) {
            form.setValue(
                'translations',
                settingsData.languages.map((lang, index) => ({
                    language: lang,
                    name: translations[index]?.name || '',
                    description: translations[index]?.description || '',
                })),
            );
        }
    }, [settingsData, form]);

    React.useEffect(() => {
        if (
            (limit_access_per_capacity && capacity_limits.length === 0) ||
            (!limit_access_per_capacity && capacity_limits.length > 0)
        ) {
            const capacityLimitsValue = limit_access_per_capacity
                ? [{ visitor_categories: [], spaces_count: '' as unknown as number }]
                : [];
            form.setValue('capacity_limits', capacityLimitsValue);
        }

        if (!limit_user_access) form.setValue('user_access', []);

        const spacesCount = (total_spaces ?? 0) + (overflow_spaces ?? 0);
        form.setValue('spaces_count', spacesCount, {
            shouldDirty: false,
            shouldValidate: false,
        });
    }, [
        limit_access_per_capacity,
        limit_user_access,
        overflow_spaces,
        form,
        total_spaces,
        capacity_limits.length
    ]);

    const calculateCategoryOptions = React.useCallback(
        (index: number) => {
            const typesToFilter = capacity_limits
                .filter((_, capacityIndex) => index !== capacityIndex)
                .flatMap((limit) => limit.visitor_categories);
            const options = Object.values(ZONE_CAPACITY_TYPE).filter(
                (type: ZONE_CAPACITY_TYPE) => !typesToFilter.includes(type),
            );
            const optionsTranslationMap = Object.entries(ZONE_CAPACITY_TRANSLATION_MAP).filter(
                ([type, _]) => !typesToFilter.includes(type as ZONE_CAPACITY_TYPE),
            );

            return {
                options,
                optionsTranslationMap,
            };
        },
        [capacity_limits],
    );

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)} variant="dialog">
            {translationFields.map(({ key, label }) =>
                fields.map((_, index) =>
                    key === 'name' ? (
                        <InputFormField<ZoneFormSchemaValues>
                            form={form}
                            name={`translations.${index}.${key}`}
                            inlineTitle={settingsData?.languages[index].toUpperCase() || ''}
                            label={formatMessage(label)}
                            key={`${index}.${key}`}
                        />
                    ) : (
                        <TextareaFormField<ZoneFormSchemaValues>
                            form={form}
                            name={`translations.${index}.${key}`}
                            inlineTitle={settingsData?.languages[index].toUpperCase() || ''}
                            label={formatMessage(label)}
                            key={`${index}.${key}`}
                        />
                    ),
                ),
            )}
            <SelectFormField<ZoneFormSchemaValues>
                form={form}
                name="type"
                label={formatMessage(zoneMessages.type)}
                options={Object.values(ZONE_TYPE)}
                optionTranslationMap={ZONE_TRANSLATION_MAP}
            />
            <InputFormField<ZoneFormSchemaValues>
                form={form}
                name="total_spaces"
                type="number"
                label={formatMessage(zoneMessages.totalSpaces)}
                min={0}
            />
            <InputFormField<ZoneFormSchemaValues>
                form={form}
                name="overflow_spaces"
                type="number"
                label={formatMessage(zoneMessages.overflowSpaces)}
                min={0}
            />
            <InputFormField<ZoneFormSchemaValues>
                form={form}
                name="spaces_count"
                disabled
                type="number"
                required={false}
                label={formatMessage(zoneMessages.spacesCount)}
                min={0}
            />
            <SelectFormField<ZoneFormSchemaValues>
                form={form}
                name="site"
                label={formatMessage(zoneMessages.site)}
                options={siteOptions}
            />
            <FormField
                control={form.control}
                name="visitor_categories"
                render={({ field }) => {
                    const fieldData = ((field.value ?? []) as VISITOR_CATEGORY_TYPE[])
                        .map((category) => VISITOR_CATEGORIES_TRANSLATION_MAP[category])
                        .filter(Boolean)
                        .map((msg) => formatMessage(msg!))
                        .join(', ');

                    const allValues = Object.keys(VISITOR_CATEGORIES_TRANSLATION_MAP) as VISITOR_CATEGORY_TYPE[];
                    return (
                        <FormItem variant="dialog">
                            <FormLabel variant="dialog" required>
                                <FormattedMessage {...zoneMessages.visitorCategories} />
                            </FormLabel>
                            <FormControlStack>
                                <FormControl>
                                    <MultiSelect
                                        value={(field.value ?? []) as VISITOR_CATEGORY_TYPE[]}
                                        onChange={(values) => field.onChange(values)}
                                    >
                                        <MultiSelectTrigger asChild>
                                            <MultiSelectControl className="-mr-4 w-full">
                                                {fieldData}
                                                {fieldData.length == 0 && (
                                                    <span className="text-gray-400">
                                                        {`${formatMessage(zoneMessages.visitorCategories)}...`}
                                                    </span>
                                                )}
                                            </MultiSelectControl>
                                        </MultiSelectTrigger>
                                        <MultiSelectContent allValues={allValues}>
                                            {Object.entries(VISITOR_CATEGORIES_TRANSLATION_MAP).map(([key, value]) => (
                                                <MultiSelectOption key={key} selected={key}>
                                                    <div className="flex flex-row justify-between">
                                                        {formatMessage(value)}
                                                    </div>
                                                </MultiSelectOption>
                                            ))}
                                        </MultiSelectContent>
                                    </MultiSelect>
                                </FormControl>
                                <FormMessage />
                            </FormControlStack>
                        </FormItem>
                    );
                }}
            />
            <SelectFormField<ZoneFormSchemaValues>
                form={form}
                name="service_provider"
                label={formatMessage(zoneMessages.organisation)}
                options={serviceProviderOptions}
            />
            <SelectFormField<ZoneFormSchemaValues>
                form={form}
                name="parent_zone"
                required={false}
                label={formatMessage(zoneMessages.parentZone)}
                options={zoneOptions}
            />
            <SelectFormField<ZoneFormSchemaValues>
                form={form}
                name="limit_access_per_capacity"
                isBoolean
                required
                label={formatMessage(zoneMessages.limitAccessCapacity)}
                options={BOOLEAN_OPTIONS}
                optionTranslationMap={BOOLEAN_MAP}
            />
            <SelectFormField<ZoneFormSchemaValues>
                form={form}
                name="limit_user_access"
                isBoolean
                required
                label={formatMessage(zoneMessages.limitUserAccess)}
                options={BOOLEAN_OPTIONS}
                optionTranslationMap={BOOLEAN_MAP}
            />
            <FormField
                control={form.control}
                name="user_access"
                render={({ field }) => {
                    const fieldData = field.value
                        .map((user) => usersData?.results.find((u) => u.id === user)?.name)
                        .join(', ');

                    const allValues = usersData?.results.map((point) => point.id);
                    return (
                        <FormItem variant="dialog">
                            <FormLabel variant="dialog">
                                <FormattedMessage
                                    id={zoneMessages.userAccess.id}
                                    defaultMessage={zoneMessages.userAccess.defaultMessage}
                                />
                            </FormLabel>
                            <FormControlStack>
                                <FormControl>
                                    <MultiSelect {...field}>
                                        <MultiSelectTrigger asChild>
                                            <MultiSelectControl disabled={!limit_user_access}>
                                                {fieldData}
                                                {fieldData.length == 0 && (
                                                    <span className="text-gray-400">
                                                        <FormattedMessage
                                                            id={zoneMessages.userAccess.id}
                                                            defaultMessage={zoneMessages.userAccess.defaultMessage}
                                                        />
                                                        ...
                                                    </span>
                                                )}
                                            </MultiSelectControl>
                                        </MultiSelectTrigger>
                                        <MultiSelectContent allValues={allValues}>
                                            {usersData?.results.map((user) => (
                                                <MultiSelectOption key={user.id} selected={user.id}>
                                                    {user.name}
                                                </MultiSelectOption>
                                            ))}
                                        </MultiSelectContent>
                                    </MultiSelect>
                                </FormControl>
                                <FormMessage />
                            </FormControlStack>
                        </FormItem>
                    );
                }}
            />
            {capacity_limits?.length > 0 && (
                <>
                    <Separator className="mt-2 mb-4" />
                    {capacity_limits?.map((_, index) => (
                        <>
                            <FormField
                                control={form.control}
                                name={`capacity_limits.${index}.visitor_categories`}
                                render={({ field }) => {
                                    // Remove ref from field to avoid passing it to the MultiSelect component
                                    const { ref: _, ...rest } = field;
                                    const { options, optionsTranslationMap } = calculateCategoryOptions(index);
                                    const fieldData = field.value
                                        .map((type) => formatMessage(ZONE_CAPACITY_TRANSLATION_MAP[type]))
                                        .join(', ');

                                    return (
                                        <FormItem variant="dialog">
                                            <FormLabel variant="dialog" required>
                                                {formatMessage(zoneMessages.visitorSpacesLimit)}
                                            </FormLabel>
                                            <div key={index} className="flex gap-2 pb-1">
                                                <div className="grid w-full grid-cols-[2fr_1fr] gap-2">
                                                    <FormControlStack>
                                                        <FormControl>
                                                            <MultiSelect {...rest}>
                                                                <MultiSelectTrigger asChild>
                                                                    <MultiSelectControl>
                                                                        {fieldData}
                                                                        {fieldData.length == 0 && (
                                                                            <span className="text-gray-400">
                                                                                <FormattedMessage
                                                                                    id={zoneMessages.categories.id}
                                                                                    defaultMessage={
                                                                                        zoneMessages.categories
                                                                                            .defaultMessage
                                                                                    }
                                                                                />
                                                                                ...
                                                                            </span>
                                                                        )}
                                                                    </MultiSelectControl>
                                                                </MultiSelectTrigger>
                                                                <MultiSelectContent allValues={options}>
                                                                    {optionsTranslationMap.map(([key, value]) => (
                                                                        <MultiSelectOption key={key} selected={key}>
                                                                            {formatMessage(value)}
                                                                        </MultiSelectOption>
                                                                    ))}
                                                                </MultiSelectContent>
                                                            </MultiSelect>
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormControlStack>
                                                    <InputFormField<ZoneFormSchemaValues>
                                                        form={form}
                                                        name={`capacity_limits.${index}.spaces_count` as any}
                                                        type="number"
                                                        placeholder={formatMessage(zoneMessages.count)}
                                                        min={0}
                                                    />
                                                </div>
                                                <Button
                                                    onClick={() => {
                                                        const newLimits = capacity_limits.filter(
                                                            (_, innerIndex) => innerIndex !== index,
                                                        );
                                                        if (newLimits.length < 1)
                                                            form.setValue('limit_access_per_capacity', false);
                                                        form.setValue('capacity_limits', newLimits);
                                                    }}
                                                    variant="destructiveLight"
                                                    size="icon"
                                                    className="shrink-0"
                                                    type="button"
                                                >
                                                    <FontAwesomeIcon icon={ICON_MAP.DELETE} />
                                                </Button>
                                            </div>
                                        </FormItem>
                                    );
                                }}
                            />
                        </>
                    ))}
                    <div className="flex flex-col gap-2 md:flex-row md:gap-4">
                        <div className="w-full md:max-w-36 md:text-right" />
                        <FormControl>
                            <Button
                                className="flex-1"
                                variant="outline"
                                onClick={() =>
                                    form.setValue('capacity_limits', [
                                        ...capacity_limits,
                                        { visitor_categories: [], spaces_count: '' as unknown as number },
                                    ])
                                }
                                type="button"
                            >
                                <FontAwesomeIcon className="text-primary-700" icon={ICON_MAP.ADD} />
                                <FormattedMessage
                                    id={zoneMessages.addVisitorLimit.id}
                                    defaultMessage={zoneMessages.addVisitorLimit.defaultMessage}
                                />
                            </Button>
                        </FormControl>
                    </div>
                </>
            )}
        </Form>
    );
}

export default ZoneForm;
